import React, { useState, useEffect } from 'react';
import { ArrowLeft, Upload, X } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';

const ApplyNow = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const prefilledData = location.state?.prefilledData || {};
  
  const [formData, setFormData] = useState({
    // Personal Information
    name: '',
    email: '',
    phone: '',
    
    // Property Description
    propertyName: '',
    propertyDescription: '',
    location: '',
    propertyCategory: '',
    propertyFor: '',
    price: '',
    roomType: '',
    
    // Property Features
    propertyFeatures: '',
    
    // Property Details
    size: '',
    bedrooms: '',
    bathrooms: '',
    floor: '',
    additionalSpace: '',
    furnishing: '',
    ceilingHeight: '',
    constructionYear: '',
    renovation: '',
    
    // Property Utility
    heating: '',
    airCondition: '',
    fireplace: '',
    elevator: '',
    ventilation: '',
    intercom: '',
    windowType: '',
    cableTV: '',
    wifi: '',
    
    // Additional features (checkboxes)
    parking: false,
    security: false,
    garden: false,
    pool: false,
    gym: false,
    
    // Message
    message: ''
  });

  const [images, setImages] = useState([]);

  // Pre-fill form with data from modal
  useEffect(() => {
    if (prefilledData && Object.keys(prefilledData).length > 0) {
      setFormData(prev => ({
        ...prev,
        location: prefilledData.location || '',
        propertyName: prefilledData.propertyName || '',
        roomType: prefilledData.roomType || '',
        price: prefilledData.price?.replace(/[^0-9]/g, '') || ''
      }));
    }
  }, [prefilledData]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    const newImages = files.map(file => ({
      file,
      preview: URL.createObjectURL(file)
    }));
    setImages(prev => [...prev, ...newImages]);
  };

  const removeImage = (index) => {
    setImages(prev => {
      const newImages = [...prev];
      URL.revokeObjectURL(newImages[index].preview);
      newImages.splice(index, 1);
      return newImages;
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Basic validation for required fields
    const requiredFields = ['name', 'email', 'phone', 'propertyName', 'location', 'propertyCategory', 'propertyFor', 'price'];
    const missingFields = requiredFields.filter(field => !formData[field]);
    
    if (missingFields.length > 0) {
      alert(`Please fill in all required fields: ${missingFields.join(', ')}`);
      return;
    }
    
    console.log('Property listing submitted:', formData);
    console.log('Images:', images);
    alert('Property listing submitted successfully! Our team will review it and get back to you within 24-48 hours.');
    
    // Reset form
    setFormData({
      name: '',
      email: '',
      phone: '',
      propertyName: '',
      propertyDescription: '',
      location: '',
      propertyCategory: '',
      propertyFor: '',
      price: '',
      roomType: '',
      propertyFeatures: '',
      size: '',
      bedrooms: '',
      bathrooms: '',
      floor: '',
      additionalSpace: '',
      furnishing: '',
      ceilingHeight: '',
      constructionYear: '',
      renovation: '',
      heating: '',
      airCondition: '',
      fireplace: '',
      elevator: '',
      ventilation: '',
      intercom: '',
      windowType: '',
      cableTV: '',
      wifi: '',
      parking: false,
      security: false,
      garden: false,
      pool: false,
      gym: false,
      message: ''
    });
    setImages([]);
  };

  const allLocations = [
    'Johannesburg', 'Benoni', 'Pretoria', 'Witbank'
  ];

  return (
    <div className="min-h-screen bg-gray-50 pt-20 pb-12 mt-[25px]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="font-medium">Back</span>
        </button>

        {/* Header */}
        <div className="text-center mb-8 sm:mb-12">
          <span className="inline-block text-xs sm:text-sm text-orange-600 uppercase tracking-wider font-semibold mb-3">
            LIST YOUR PROPERTY
          </span>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            List your property with Raulfex
          </h1>
          <p className="text-gray-600 text-base sm:text-lg max-w-2xl mx-auto">
            Reach thousands of potential buyers by listing your property on our platform. Our team will guide you through the process to ensure a smooth and successful sale.
          </p>
          {prefilledData && Object.keys(prefilledData).length > 0 && (
            <div className="mt-4 p-4 bg-orange-50 rounded-lg border border-orange-200">
              <p className="text-sm text-orange-800">
                ✓ Your selection has been pre-filled. Please complete the remaining information below.
              </p>
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-lg p-6 sm:p-8 space-y-8">
          {/* Personal Information */}
          <div>
            <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-6">Personal Information</h2>
            <div className="grid sm:grid-cols-2 gap-4 sm:gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Your name"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-all"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="your.email@example.com"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-all"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="+27 XX XXX XXXX"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-all"
                />
              </div>
            </div>
          </div>

          {/* Property Description */}
          <div>
            <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-6">Property Description</h2>
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Property Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="propertyName"
                  value={formData.propertyName}
                  onChange={handleInputChange}
                  placeholder="e.g., Modern Family Home, Office Space, etc."
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-all"
                />
              </div>

              {formData.roomType && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Room Type
                  </label>
                  <input
                    type="text"
                    name="roomType"
                    value={formData.roomType}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-all bg-orange-50"
                    readOnly
                  />
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Property Description</label>
                <textarea
                  name="propertyDescription"
                  value={formData.propertyDescription}
                  onChange={handleInputChange}
                  placeholder="Provide a detailed description of your property..."
                  rows={5}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none resize-vertical transition-all"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Location <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  {allLocations.map((city) => (
                    <label key={city} className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="location"
                        value={city}
                        checked={formData.location === city}
                        onChange={handleInputChange}
                        className="w-4 h-4 text-orange-500 focus:ring-orange-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">{city}</span>
                    </label>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Property Category <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {['Residential', 'Commercial', 'Industrial'].map((category) => (
                    <label 
                      key={category} 
                      className={`flex items-center justify-center px-4 py-3 border-2 rounded-lg cursor-pointer transition-all ${
                        formData.propertyCategory === category
                          ? 'border-orange-500 bg-orange-50 text-orange-700'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      <input
                        type="radio"
                        name="propertyCategory"
                        value={category}
                        checked={formData.propertyCategory === category}
                        onChange={handleInputChange}
                        className="sr-only"
                      />
                      <span className="font-medium">{category}</span>
                    </label>
                  ))}
                </div>
              </div>
              
              <div className="grid sm:grid-cols-2 gap-4 sm:gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Property For <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="propertyFor"
                    value={formData.propertyFor}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-all"
                  >
                    <option value="">Select...</option>
                    <option value="Sale">For Sale</option>
                    <option value="Rent">For Rent</option>
                    <option value="Lease">For Lease</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Price (ZAR) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={handleInputChange}
                    placeholder="0"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-all"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Property Images */}
          <div>
            <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-6">Property Images</h2>
            <div className="space-y-4">
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-orange-500 transition-colors">
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageUpload}
                  className="hidden"
                  id="image-upload"
                />
                <label htmlFor="image-upload" className="cursor-pointer">
                  <Upload className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                  <p className="text-sm text-gray-600">
                    <span className="text-orange-500 font-semibold">Click to upload</span> or drag and drop
                  </p>
                  <p className="text-xs text-gray-500 mt-1">PNG, JPG up to 10MB</p>
                </label>
              </div>

              {images.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {images.map((img, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={img.preview}
                        alt={`Property ${index + 1}`}
                        className="w-full h-32 object-cover rounded-lg"
                      />
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Property Details */}
          <div>
            <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-6">Property Details</h2>
            <div className="grid sm:grid-cols-2 gap-4 sm:gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Size (m²)</label>
                <input
                  type="number"
                  name="size"
                  value={formData.size}
                  onChange={handleInputChange}
                  placeholder="0"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-all"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Bedrooms</label>
                <input
                  type="number"
                  name="bedrooms"
                  value={formData.bedrooms}
                  onChange={handleInputChange}
                  placeholder="0"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-all"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Bathrooms</label>
                <input
                  type="number"
                  name="bathrooms"
                  value={formData.bathrooms}
                  onChange={handleInputChange}
                  placeholder="0"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-all"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Floor</label>
                <input
                  type="text"
                  name="floor"
                  value={formData.floor}
                  onChange={handleInputChange}
                  placeholder="e.g., Ground, 2nd, etc."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-all"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Furnishing</label>
                <select
                  name="furnishing"
                  value={formData.furnishing}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-all"
                >
                  <option value="">Select...</option>
                  <option value="Unfurnished">Unfurnished</option>
                  <option value="Semi-furnished">Semi-furnished</option>
                  <option value="Fully furnished">Fully furnished</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Year Built</label>
                <input
                  type="number"
                  name="constructionYear"
                  value={formData.constructionYear}
                  onChange={handleInputChange}
                  placeholder="YYYY"
                  min="1900"
                  max={new Date().getFullYear()}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-all"
                />
              </div>
            </div>
          </div>

          {/* Additional Features */}
          <div>
            <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-6">Additional Features</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
              {[
                { name: 'parking', label: 'Parking' },
                { name: 'security', label: '24/7 Security' },
                { name: 'garden', label: 'Garden' },
                { name: 'pool', label: 'Swimming Pool' },
                { name: 'gym', label: 'Gym' }
              ].map((feature) => (
                <label
                  key={feature.name}
                  className={`flex items-center justify-center px-4 py-3 border-2 rounded-lg cursor-pointer transition-all ${
                    formData[feature.name]
                      ? 'border-orange-500 bg-orange-50 text-orange-700'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <input
                    type="checkbox"
                    name={feature.name}
                    checked={formData[feature.name]}
                    onChange={handleInputChange}
                    className="sr-only"
                  />
                  <span className="text-sm font-medium">{feature.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Property Utilities */}
          <div>
            <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-6">Property Utilities</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Air Conditioning</label>
                <select
                  name="airCondition"
                  value={formData.airCondition}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-all"
                >
                  <option value="">Select...</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Elevator</label>
                <select
                  name="elevator"
                  value={formData.elevator}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-all"
                >
                  <option value="">Select...</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">WiFi</label>
                <select
                  name="wifi"
                  value={formData.wifi}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-all"
                >
                  <option value="">Select...</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </div>
            </div>
          </div>

          {/* Additional Message */}
          <div>
            <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-6">Additional Information</h2>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleInputChange}
              placeholder="Any additional information about the property..."
              rows={5}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none resize-vertical transition-all"
            />
          </div>

          {/* Submit Button */}
          <div className="pt-4">
            <button
              type="submit"
              className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-4 px-6 rounded-lg transition-colors duration-200 focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 outline-none shadow-lg hover:shadow-xl"
            >
              Submit Property Listing
            </button>
            <p className="text-center text-sm text-gray-500 mt-4">
              By submitting, you agree to our terms and conditions
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ApplyNow;